<template>
  <div class="-mx-4">
    <div class="-mx-2 mb-6">
      <link-secondary
        href="/searches"
      >
        Done
     </link-secondary>
    </div>

    <!-- Landed Positions Section -->
    <div class="w-full mb-8 -mx-4">
      <div class="bg-gradient-to-br from-emerald-50 to-green-50 overflow-hidden shadow-md sm:rounded-2xl border-2 border-emerald-200">
        <div class="px-6 py-8 sm:px-10">
          <div class="flex items-center gap-3 mb-6">
            <svg class="w-8 h-8 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <h2 class="text-2xl font-bold text-gray-900">
              Landed Positions
            </h2>
          </div>

          <div
            v-if="positions.length === 0"
            class="text-center py-12"
          >
            <svg class="mx-auto h-16 w-16 text-gray-400 mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <p class="text-gray-600 text-lg">No landed positions yet</p>
          </div>

          <ul v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
            <li
              v-for="position in sortedPositions"
              :key="position.id"
              class="relative col-span-1 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <div class="w-full flex items-start justify-between p-6 space-x-6">
                <div class="flex-1 w-full">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span class="text-emerald-700 text-sm font-semibold">{{ position.candidate.name.charAt(0) }}</span>
                    </div>
                    <h3 class="text-gray-900 text-base font-semibold">
                      {{ position.candidate.name }}
                    </h3>
                  </div>
                  <div class="flex flex-wrap gap-2 items-start">
                    <span
                      v-if="position.left_in_guarantee_period"
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                    >
                      <FontAwesomeIcon
                        :icon="faExclamationTriangle"
                        class="h-3 w-3"
                      />
                      Left in Guarantee
                    </span>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
                    >
                      Started {{ (new Date(position.created_at)).toLocaleDateString() }}
                    </span>
                    <span
                      v-if="!position.in_role"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      Ended {{ (new Date(position.updated_at)).toLocaleDateString() }}
                    </span>
                  </div>
                </div>

                <div class="flex gap-1 flex-shrink-0">
                  <DeleteButton
                    :emits="true"
                    @confirm="removeFromPosition(position)"
                    :dusk="`remove-from-position-${position.id}`"
                    hoverTitle="Remove from Position"
                    :disabled="position.in_role === false"
                    classNames="p-2 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-colors"
                  >
                    <FontAwesomeIcon :icon="faPersonCircleMinus" class="h-5 w-5" />
                  </DeleteButton>
                  <DeleteButton
                    :dusk="`delete-position-${position.id}`"
                    :emits="true"
                    @confirm="deletePosition(position)"
                    classNames="p-2 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <FontAwesomeIcon :icon="faTrashCan" class="h-4 w-4" />
                  </DeleteButton>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Assigned Candidates Section -->
    <div class="w-full mb-8 -mx-4">
      <div class="bg-gradient-to-br from-indigo-50 to-purple-50 overflow-hidden shadow-md sm:rounded-2xl border-2 border-indigo-200">
        <div class="px-6 py-8 sm:px-10">
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center gap-3">
              <svg class="w-8 h-8 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
              </svg>
              <h2 class="text-2xl font-bold text-gray-900">
                Assigned Candidates
              </h2>
            </div>
          </div>

          <div class="mb-6">
            <SelectSingle
              dusk="availableCandidates"
              :options="availableCandidates"
              @singleSelect="assignCandidate"
              :emitOnly="true"
              placeholder="Assign a Candidate"
              optionsLabel="name"
              searchKey="name"
            />
          </div>

          <div
            v-if="assignedCandidates.length === 0"
            class="text-center py-12"
          >
            <svg class="mx-auto h-16 w-16 text-gray-400 mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
            </svg>
            <p class="text-gray-600 text-lg">No candidates assigned to this search yet</p>
          </div>

          <ul v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
            <li
              v-for="candidate in sortedAssignedCandidates"
              :key="candidate.id"
              class="col-span-1 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <div class="w-full flex items-center justify-between p-6">
                <div class="flex-1 flex items-center gap-3">
                  <div class="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span class="text-indigo-700 text-sm font-semibold">{{ candidate.name.charAt(0) }}</span>
                  </div>
                  <h3 class="text-gray-900 text-base font-semibold">
                    {{ candidate.name }}
                  </h3>
                </div>
              </div>
              <div class="border-t border-gray-200">
                <div class="-mt-px flex">
                  <button
                    @click="removeCandidate(candidate)"
                    :dusk="`remove-candidate-${candidate.id}`"
                    class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center gap-2 py-4 text-sm font-medium text-gray-700 border-r border-gray-200 rounded-bl-xl hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:z-10 transition-colors"
                  >
                    <FontAwesomeIcon :icon="faMagnifyingGlassMinus" class="h-4 w-4" />
                    <span>Remove</span>
                  </button>
                  <button
                    @click="fillPosition(candidate)"
                    :dusk="`fill-position-${candidate.id}`"
                    class="relative w-0 flex-1 inline-flex items-center justify-center gap-2 py-4 text-sm font-medium text-gray-700 rounded-br-xl hover:text-emerald-600 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:z-10 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white transition-colors"
                  >
                    <FontAwesomeIcon :icon="faPersonCirclePlus" class="h-4 w-4" />
                    <span>Fill Position</span>
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
  import { ref, defineProps, computed } from 'vue';
  import axios from 'axios';
  import LinkSecondary from './LinkSecondary.vue';
  import DeleteButton from './DeleteButton.vue';
  import SelectSingle from './SelectSingle.vue';
  import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
  import {
    faMagnifyingGlassMinus,
    faPersonCirclePlus,
    faPersonCircleMinus,
    faTrashCan,
    faExclamationTriangle
  } from '@fortawesome/pro-regular-svg-icons';

  const props = defineProps({
    search: Object,
    candidates: Array,
  })

  const csrf = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

  const assignedCandidates = ref(props.search.candidates);
  const positions = ref(props.search.positions);

  const availableCandidates = computed(function(){
    const assignedIds = assignedCandidates.value.map( s => s.id );
    const positionsIds = positions.value.map( p => p.candidate.id );
    const allCandidates = [...props.candidates, ...assignedCandidates.value];
    const candidates = Array.from(new Map(allCandidates.map(c => [c.id, c])).values());

    return candidates
      .filter( s => !assignedIds.includes(s.id) )
      .filter( s => !positionsIds.includes(s.id) )
      .sort( (a, b) => a.name.localeCompare(b.name) );
  });

  const sortedAssignedCandidates = computed(function(){
    let positionsIds = positions.value.map( p => p.candidate.id );
    return assignedCandidates.value
      .filter( s => !positionsIds.includes(s.id) )
      .sort( (a, b) =>  a.name.localeCompare(b.name) );
  })

  const sortedPositions = computed(function(){
  console.log(positions.value);
    return positions.value
      .sort( (a, b) => a.created_at.localeCompare(b.created_at) );
  });

  async function assignCandidate(candidate){
    assignedCandidates.value = [...assignedCandidates.value, candidate];

    const {data} = await axios.post(`/searches/${props.search.id}/manage`, {
      assign: candidate.id,
      _token: csrf
    });

    assignedCandidates.value = data.search.candidates;
  };

  async function removeCandidate(candidate){
    assignedCandidates.value = assignedCandidates.value.filter( c => c.id !== candidate.id );

    const {data} = await axios.post(`/searches/${props.search.id}/manage`, {
      remove: candidate.id,
      _token: csrf
    });

    assignedCandidates.value = data.search.candidates;
  };

  async function fillPosition(candidate){
    positions.value = [...positions.value, {candidate, in_role: true}];

    const {data} = await axios.post(`/searches/${props.search.id}/manage`, {
      fill: candidate.id,
      _token: csrf
    });

    positions.value = data.search.positions;
  };

  async function removeFromPosition(position){
    positions.value = positions.value.map( p => {
      if(p.candidate.id === position.candidate.id){
        return {...p, in_role: false};
      }
      return p;
    });

    const {data} = await axios.post(`/searches/${props.search.id}/manage`, {
      unfill: position.id,
      _token: csrf
    });

    positions.value = data.search.positions;
  }

  async function deletePosition(position){
    positions.value = positions.value.filter( p => p.id !== position.id );

    const {data} = await axios.post(`/searches/${props.search.id}/manage`, {
      delete_position: position.id,
      _token: csrf
    });

    positions.value = data.search.positions;
  }

</script>
